import { HttpHeaders } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { BehaviorSubject, EMPTY, from, Observable, of } from 'rxjs';
import { map, pluck, retry, switchMap, tap } from 'rxjs/operators';
import { API } from '../constants/api.constant';
import { ResponseInfo } from '../models/http.model';
import {
  CheckFeeResponse,
  FxRate,
  IdDocumentPayload,
  Payer,
  Point,
  RecentTransaction,
  Recipient,
  ServiceType,
  TransactionDetail,
  TransactionHistory,
  TransactionInfo,
  TransactionMessage,
} from '../models/transaction.model';
import { CustomHttpClientService } from './custom-http-client.service';
import { UserService } from './user.service';


@Injectable({
  providedIn: 'root',
})
export class TransactionService {
  constructor(
    private http: CustomHttpClientService,
    private userService: UserService
  ) { }

  public steps$ = this.getSteps();

  public readonly currencyIcons = {
    CAD: 'assets/icons/countries/Canada.svg',
    INR: 'assets/icons/countries/India.svg',
    PHP: 'assets/icons/countries/Philippines.svg',
    LKR: 'assets/icons/countries/Sri Lanka.svg',
    CNY: 'assets/icons/countries/China.svg',
    NPR: 'assets/icons/countries/Nepal.svg',
    KRW: 'assets/icons/countries/South Korea.svg',
    SGD: 'assets/icons/countries/Singapore.svg',
    MXN: 'assets/icons/countries/Mexico.svg',
    MYR: 'assets/icons/countries/Malaysia.svg',
    JPY: 'assets/icons/countries/Japan.svg',
    ILS: 'assets/icons/countries/Israel.svg',
    EUR: 'assets/icons/countries/France.svg',
    GBP: 'assets/icons/countries/United Kingdom.svg',
    AUD: 'assets/icons/countries/Australia.svg',
    HKD: 'assets/icons/countries/Hong Kong.svg',
  };

  public readonly countryIcons = {
    Canada: 'assets/icons/countries/Canada.svg',
    India: 'assets/icons/countries/India.svg',
    Philippines: 'assets/icons/countries/Philippines.svg',
    'Sri Lanka': 'assets/icons/countries/Sri Lanka.svg',
    China: 'assets/icons/countries/China.svg',
    Nepal: 'assets/icons/countries/Nepal.svg',
    'South Korea': 'assets/icons/countries/South Korea.svg',
    Singapore: 'assets/icons/countries/Singapore.svg',
    Mexico: 'assets/icons/countries/Mexico.svg',
    Malaysia: 'assets/icons/countries/Malaysia.svg',
    Japan: 'assets/icons/countries/Japan.svg',
    Israel: 'assets/icons/countries/Israel.svg',
    France: 'assets/icons/countries/France.svg',
    'United Kingdom': 'assets/icons/countries/United Kingdom.svg',
    Australia: 'assets/icons/countries/Australia.svg',
    'Hong Kong': 'assets/icons/countries/Hong Kong.svg',

    '加拿大': 'assets/icons/countries/Canada.svg',
    '印度': 'assets/icons/countries/India.svg',
    '菲律宾': 'assets/icons/countries/Philippines.svg',
    '斯里兰卡': 'assets/icons/countries/Sri Lanka.svg',
    '中国': 'assets/icons/countries/China.svg',
    '尼泊尔': 'assets/icons/countries/Nepal.svg',
    '韩国': 'assets/icons/countries/South Korea.svg',
    '新加坡': 'assets/icons/countries/Singapore.svg',
    '墨西哥': 'assets/icons/countries/Mexico.svg',
    '马来西亚': 'assets/icons/countries/Malaysia.svg',
    '日本': 'assets/icons/countries/Japan.svg',
    '以色列': 'assets/icons/countries/Israel.svg',
    '法国': 'assets/icons/countries/France.svg',
    '英国': 'assets/icons/countries/United Kingdom.svg',
    '澳大利亚': 'assets/icons/countries/Australia.svg',
    '香港': 'assets/icons/countries/Hong Kong.svg',

    'CAN': 'assets/icons/countries/Canada.svg',
    'IND': 'assets/icons/countries/India.svg',
    'PHL': 'assets/icons/countries/Philippines.svg',
    'LKA': 'assets/icons/countries/Sri Lanka.svg',
    'CHN': 'assets/icons/countries/China.svg',
    'NPL': 'assets/icons/countries/Nepal.svg',
    'KOR': 'assets/icons/countries/South Korea.svg',
    'SGP': 'assets/icons/countries/Singapore.svg',
    'MEX': 'assets/icons/countries/Mexico.svg',
    'MYS': 'assets/icons/countries/Malaysia.svg',
    'JPN': 'assets/icons/countries/Japan.svg',
    'ISR': 'assets/icons/countries/Israel.svg',
    'FRA': 'assets/icons/countries/France.svg',
    'GBR': 'assets/icons/countries/United Kingdom.svg',
    'AUS': 'assets/icons/countries/Australia.svg',
    'HKG': 'assets/icons/countries/Hong Kong.svg',

    'CA': 'assets/icons/countries/Canada.svg',
    'IN': 'assets/icons/countries/India.svg',
    'PH': 'assets/icons/countries/Philippines.svg',
    'LK': 'assets/icons/countries/Sri Lanka.svg',
    'CN': 'assets/icons/countries/China.svg',
    'NP': 'assets/icons/countries/Nepal.svg',
    'KR': 'assets/icons/countries/South Korea.svg',
    'SG': 'assets/icons/countries/Singapore.svg',
    'MX': 'assets/icons/countries/Mexico.svg',
    'MY': 'assets/icons/countries/Malaysia.svg',
    'JP': 'assets/icons/countries/Japan.svg',
    'IL': 'assets/icons/countries/Israel.svg',
    'FR': 'assets/icons/countries/France.svg',
    'GB': 'assets/icons/countries/United Kingdom.svg',
    'AU': 'assets/icons/countries/Australia.svg',
    'HK': 'assets/icons/countries/Hong Kong.svg',
  };

  public pin: string;
  public transaction: TransactionInfo;
  public serviceFee: Array<{
    serviceName: ServiceType;
    fee: number;
  }>;
  public selectedRecentTransaction: RecentTransaction;
  public selectedRecipient: Recipient;
  public selectedCountryIsoCode: string;
  public selectedPayer: Payer;
  public enabledOptions = {
    CashPickup: false,
    MobileWallet: false,
    BankAccount: false,
  };
  public submitResponse: any;
  public senderIDRequried = false;
  public signedPdf;
  public tempPdf;
  public fileNames: string;
  public tempPdfUrl;
  public signature;
  public unreadMessageCount$ = new BehaviorSubject(0);
  public showHistoryVerifyPin$ = new BehaviorSubject(false);
  public historyRecord$: Observable<TransactionHistory[]> = EMPTY;
  public recipients$: Observable<Recipient[]> = EMPTY;
  public transactionFee = null;
  private newRecipient: Recipient;
  public selectedCountry = null;
  public currentCountryPayers: Payer[] = [];
  public usePointsToWaiveFee$$ = signal<boolean>(false);
  public creatingNewRecipient = false;
  public refreshRecentRecipients$ = new BehaviorSubject<boolean>(false);
  public triggerSpecialReferralOfferHint$ = new BehaviorSubject<boolean>(false);

  // 打开interac页面完成支付后收到的result callback
  public interacResult$: BehaviorSubject<URLSearchParams> = new BehaviorSubject(
    null
  );

  public getSteps(): Observable<Array<any>> {
    return of([
      'tabs.transaction.step.amount',
      'tabs.transaction.step.time',
      'tabs.transaction.step.method',
      'tabs.transaction.step.recipient',
      'tabs.transaction.step.payment',
    ]);
  }

  public initTransaction() {
    // init/reset transaction
    this.transactionFee = null;
    this.senderIDRequried = false;
    this.selectedPayer = null;
    this.currentCountryPayers = [];
    this.submitResponse = null;
    this.signedPdf = null;
    this.tempPdf = null;
    this.signature = null;
    this.usePointsToWaiveFee = false;
    const userProfile = this.userService.userProfile$.getValue();
    if (userProfile) {
      this.transaction = new TransactionInfo({
        firstname: userProfile.user.cfirstname,
        middlename: userProfile.user.cmiddlename,
        lastname: userProfile.user.clastname,
      });
    } else {
      this.transaction = new TransactionInfo();
    }
  }

  private syncSenderInfo() {
    const userProfile = this.userService.userProfile$.getValue();
    if (!userProfile?.user || !this.transaction?.inBound) {
      return;
    }

    this.transaction.inBound.personFirstName = userProfile.user.cfirstname || null;
    this.transaction.inBound.personMiddleName = userProfile.user.cmiddlename || null;
    this.transaction.inBound.personLastName = userProfile.user.clastname || null;
  }

  get usePointsToWaiveFee() {
    return this.usePointsToWaiveFee$$();
  }

  set usePointsToWaiveFee(value: boolean) {
    this.usePointsToWaiveFee$$.set(value);
  }

  public saveTransation() {
    this.saveIncompletedTransaction({
      content: JSON.stringify(this.transaction),
    }).subscribe();
  }

  public discardTransation() {
    this.saveIncompletedTransaction({ content: null }).subscribe();
  }

  public getSendMoneyActivities(): Observable<any> {
    return this.http.get(API.PREFIX + API.GET_SEND_MONEY_ACTIVITIES, {
      headers: new HttpHeaders({ ignoreLoading: 'ignoreLoading' }),
    }).pipe(map((res: any) => res.result));
  }

  public checkTxn(): Observable<any> {
    return this.http.get(API.PREFIX + API.CHECK_TRANSACTION);
  }

  public checkTotalAmount(amount: number): Observable<boolean> {
    return this.http
      .post(
        API.PREFIX + API.CHECK_TOTAL_AMOUNT,
        { amount },
        {
          headers: new HttpHeaders({ ignoreLoading: 'ignoreLoading' }),
        }
      )
      .pipe(map((res: ResponseInfo) => res.result.needIdVerify));
  }

  public getProvinces(countryCode: string): Observable<any> {
    return this.http.get(API.PREFIX + API.GET_PROVINCES + countryCode);
  }

  public getFxRate(payload: {
    amount: number;
    currency: string;
    payerId: string;
  }): Observable<FxRate[]> {
    const params = `?payerId=${payload.payerId}&instruments=CAD_${payload.currency}&amount=${payload.amount}`;
    return this.http
      .get(API.PREFIX + API.GET_TRANSACTION_RATE + params, {
        headers: new HttpHeaders({ ignoreLoading: 'ignoreLoading' }),
      })
      .pipe(map((res: ResponseInfo) => res.result));
  }

  public getPublicFxRate(payload: {
    amount: number;
    currency: string;
    payerId: string;
  }): Observable<FxRate[]> {
    const params = `?payerId=${payload.payerId}&instruments=CAD_${payload.currency}&amount=${payload.amount}`;
    return this.http
      .get(API.PREFIX + API.GET_PUBLIC_TRANSACTION_RATE + params, {
        headers: new HttpHeaders({ ignoreLoading: 'ignoreLoading' }),
      })
      .pipe(map((res: ResponseInfo) => res.result));
  }

  public getTransactionAmountIn24H(): Observable<number> {
    return this.http
      .get(API.PREFIX + API.GET_TRANSACTION_AMOUNT_24H)
      .pipe(map((res: any) => res.result), pluck('amount'));
  }

  public getTransactionHistory(): Observable<TransactionHistory[]> {
    return this.http
      .get(API.PREFIX + API.GET_TRANSACTION_HISTORY_LIST, {
        headers: new HttpHeaders({ ignoreLoading: 'ignoreLoading' }),
      })
      .pipe(map((res: ResponseInfo) => res.result));
  }

  public getEtransferHistoryCode(orderId: string) {
    return this.http
      .get(API.PREFIX + API.GET_TRANSACTION_HISTORY_CODE + orderId)
      .pipe(map((res: ResponseInfo) => res.result));
  }

  public modifyProfile(payload: any) {
    return this.http
      .post(API.PREFIX + API.MODIFY_PROFILE, payload)
      .pipe(map((res: ResponseInfo) => res.result));
  }

  public updateTransactionInfo(payload) {
    return this.http
      .post(API.PREFIX + API.UPDATE_TRANSACTION_INFO, payload)
      .pipe(map((res: ResponseInfo) => res.result));
  }

  public getAccountHistory(): Observable<Recipient[]> {
    return this.http
      .get(API.PREFIX + API.GET_ACCOUNT_HISTORY)
      .pipe(map((res: ResponseInfo) => res.result));
  }

  public getSenderAccountHistory(): Observable<Array<any>> {
    return this.http
      .get(API.PREFIX + API.GET_SENDER_LIST)
      .pipe(map((res: ResponseInfo) => res.result));
  }

  public deleteSender(sender: Recipient): Observable<any> {
    return this.http
      .post(API.PREFIX + API.DELETE_SENDER, { id: sender.id })
      .pipe(map((res: ResponseInfo) => res.result));
  }

  public getPointHistory(): Observable<Point[]> {
    return this.http
      .get(API.PREFIX + API.GET_POINT_HISTORY)
      .pipe(map((res: ResponseInfo) => res.result));
  }

  public getMyPromotions(): Observable<{
    availablePromotions: Array<any>;
    pastPromotions: Array<any>;
  }> {
    return this.http
      .get(API.PREFIX + API.GET_MY_PROMOTIONS)
      .pipe(map((res: ResponseInfo) => res.result));
  }

  private saveIncompletedTransaction(payload: {
    content: string;
  }): Observable<any> {
    return this.http.post(
      API.PREFIX + API.SAVE_INCOMPLETED_TRANSACTION,
      payload
    );
  }

  public getIncompletedTransaction(): Observable<any> {
    return this.http
      .get(API.PREFIX + API.GET_INCOMPLETED_TRANSACTION, {
        headers: new HttpHeaders({ ignoreLoading: 'ignoreLoading' }),
      })
      .pipe(map((res: ResponseInfo) => res.result));
  }

  public getTransactionMessages(): Observable<Array<TransactionMessage>> {
    return this.http
      .get(API.PREFIX + API.GET_TRANSACTION_MESSAGES)
      .pipe(map((res: any) => res.result));
  }

  public getPublicMessages(machineId?: string): Observable<Array<TransactionMessage>> {
    return from(Preferences.get({ key: 'MACHINE_ID' })).pipe(switchMap(result => {
      if (result.value) {
        return this.http
          .post(API.PREFIX + API.GET_PUBLIC_MESSAGES, { machine_id: machineId || result.value })
          .pipe(map((res: any) => res.result))
      } else {
        return of([]);
      }
    }), map(((res: (Array<TransactionMessage>)) => res)));
  }

  public readMessages(message: TransactionMessage): Observable<any> {
    return this.http.get(API.PREFIX + API.READ_MESSAGE + message.id, {
      headers: new HttpHeaders({ ignoreLoading: 'ignoreLoading' }),
    });
  }

  public readAllMessages(): Observable<any> {
    return this.http.get(API.PREFIX + API.READ_ALL_MESSAGES);
  }

  public deleteMessage(message: TransactionMessage): Observable<any> {
    return this.http.get(API.PREFIX + API.DELETE_MESSAGE + message.id);
  }

  public getServiceFee(): Observable<
    Array<{
      serviceName: ServiceType;
      fee: number;
    }>
  > {
    return this.http.get(API.PREFIX + API.GET_SERVICE_FEE).pipe(
      map((res: ResponseInfo) => {
        this.serviceFee = res.result;
        return res.result;
      })
    );
  }

  public getServiceFeeWithCurrency(currency: string): Observable<
    Array<{
      serviceName: ServiceType;
      fee: number;
    }>
  > {
    return this.http.get(API.PREFIX + API.GET_SERVICE_FEE + '/' + currency).pipe(
      map((res: ResponseInfo) => {
        this.serviceFee = res.result;
        return res.result;
      })
    );
  }

  public downloadPdf(fileName: string): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Accept', 'application/pdf');
    return this.http.get<Blob>('/' + API.GET_PDF + fileName, {
      headers,
      responseType: 'blob' as 'json',
    });
  }

  public fillEFTPdf() {
    let amount = 0;
    if (this.transaction.inBound.isRedeemPoint) {
      amount = +this.transaction.inBound.amount;
    } else {
      amount =
        +this.transaction.inBound.amount + +this.transactionFee.fee.inFee;
    }
    const user = this.userService.userProfile$.getValue();
    const personAddress =
      this.transaction.inBound.personAddress +
      ', ' +
      this.transaction.inBound.personCity +
      ' ' +
      this.transaction.inBound.personProvince +
      ', ' +
      this.transaction.inBound.personPostalCode;
    const bankAccountNumber =
      (this.transaction.inBound.designationNumber || '') +
      this.transaction.inBound.bankAccountNumber;

    const payload = {
      formName: 'ElectronicTransferSlip',
      operation: 'fill',
      templateFilePath: 'OB004',
      fields: [
        {
          name: 'BranchNumber',
          value: this.transaction.inBound.bankBranchNumber,
          type: 'text',
        },
        {
          name: 'Institution',
          value: this.transaction.inBound.bankInstitutionNumber,
          type: 'text',
        },
        {
          name: 'AccountNo',
          value: bankAccountNumber,
          type: 'text',
        },
        {
          name: 'BankName',
          value: this.transaction.inBound.bankName,
          type: 'text',
        },
        { name: 'Branch', value: '', type: 'text' },
        {
          name: 'BranchAddress',
          value: this.transaction.inBound.bankAddress,
          type: 'text',
        },
        {
          name: 'CityProvince',
          value:
            this.transaction.inBound.bankCity &&
              this.transaction.inBound.bankProvince
              ? `${this.transaction.inBound.bankCity} ${this.transaction.inBound.bankProvince}`
              : '',
          type: 'text',
        },
        {
          name: 'Post Code',
          value: this.transaction.inBound.bankPostalCode,
          type: 'text',
        },
        {
          name: 'Currency',
          value: this.transaction.inBound.currency,
          type: 'text',
        },
        { name: 'Amount', value: amount, type: 'text' },
        { name: 'PayerName', value: user.user.cfullname, type: 'text' },
        { name: 'Address', value: personAddress, type: 'text' },
        { name: 'Telephone', value: user.user.mobile, type: 'text' },
      ],
    };
    return this.http.post(API.PREFIX + API.FILL_PDF, payload);
  }

  public signEFTPdf() {
    const payload = {
      formName: 'ElectronicTransferSlip',
      templateFilePath: this.fileNames,
      base64Image: this.signature,
      operation: 'sign',
    };
    return this.http.post(API.PREFIX + API.SIGN_PDF, payload);
  }

  public checkFees(): Observable<CheckFeeResponse> {
    const formData = new FormData();
    this.syncSenderInfo();
    const tran = Object.assign({}, this.transaction);
    delete tran.doc;
    delete tran.quotationId;
    formData.append('data', JSON.stringify(tran));
    return this.http
      .post(API.PREFIX + API.CHECK_TRANSACTION_FEE, formData)
      .pipe(map((res: any) => res.result));
  }

  public saveNewRecipient(): Observable<any> {
    return this.http.post(API.PREFIX + API.ADD_RECEIVER, this.newRecipient);
  }

  public editRecipient(recipient: Recipient): Observable<any> {
    return this.http
      .post(API.PREFIX + API.EDIT_RECEIVER, recipient)
      .pipe(map((res: ResponseInfo) => res.result));
  }

  public deleteRecipient(recipient: Recipient): Observable<any> {
    return this.http
      .post(API.PREFIX + API.DELETE_RECEIVER, { id: recipient.id })
      .pipe(map((res: ResponseInfo) => res.result));
  }

  public getUnreadMessageCount(): Observable<any> {
    return this.http
      .get(API.PREFIX + API.GET_UNREAD_MESSAGE_COUNT, {
        headers: new HttpHeaders({ ignoreLoading: 'ignoreLoading' }),
      })
      .pipe(map((res: any) => res.result));
  }

  public getTransactionVerificationCode(): Observable<any> {
    return this.http.post(API.PREFIX + API.GET_TRANSACTION_VERIFICATION_CODE, {
      sendType: 'phone',
    });
  }

  public verifyDocument(payload: IdDocumentPayload): Observable<any> {
    return this.http
      .post(API.PREFIX + API.VERIFY_DOCUMENT, payload)
      .pipe(map((res: ResponseInfo) => res.result), retry(3),);
  }

  public verifyPassportDocument(payload: IdDocumentPayload): Observable<any> {
    return this.http
      .post(API.PREFIX + API.VERIFY_PASSPORT_DOCUMENT, payload)
      .pipe(map((res: ResponseInfo) => res.result), retry(3));
  }

  public queryInteracResult(orderId: string): Observable<any> {
    return this.http
      .post(API.PREFIX + API.QUERY_INTERAC_RESULT, { orderId })
      .pipe(map((res: ResponseInfo) => res.result));
  }

  public submitTransaction(): Observable<any> {
    const formData = new FormData();
    this.syncSenderInfo();
    formData.append('data', JSON.stringify(this.transaction));
    return this.http
      .post(API.PREFIX + API.SUBMIT_TRANSACTION, formData)
      .pipe(map((res: any) => res.result));
  }

  public getTransactionDetails(txnId: string): Observable<TransactionDetail[]> {
    return this.http
      .get(API.PREFIX + API.GET_TRANSACTION_DETAILS + `?txnId=${txnId}`)
      .pipe(map((res: any) => res.result));
  }

  public getRecent2Transactions() {
    return this.http.get(API.PREFIX + API.GET_RECENT_2_TRANSACTIONS).pipe(map((res: ResponseInfo) => res.result));
  }

  public getRecentAllTransactions() {
    return this.http.get(API.PREFIX + API.GET_RECENT_ALL_TRANSACTIONS).pipe(map((res: ResponseInfo) => res.result));
  }
}
